"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";
import { getLocaleFromPath } from "../lib/i18n/utils";
import Icon from "./Icon";

type RoomCardProps = {
  slug: string;
  title: string;
  size: number;
  people: number;
  beds: number;
  image: string | null;
};

export default function RoomCard({
  slug,
  title,
  size,
  people,
  beds,
  image,
}: RoomCardProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const locale = getLocaleFromPath(pathname);
  const bedText = beds === 1 ? t("bed") : t("beds");

  // Greek (el) is the default locale and MUST NOT appear in URLs
  // English (en) MUST be prefixed with /en
  const roomHref = locale === "el" ? `/rooms/${slug}` : `/${locale}/rooms/${slug}`;

  return (
    <Link href={roomHref} className="block w-full xl:max-w-none">
      <article className="flex h-142.5 w-full flex-col bg-ui-page">
        {image && (
          <div className="relative h-84 w-full">
            <Image
              src={image}
              alt={title}
              fill
              className="object-cover"
              sizes="(min-width: 1280px) 385px, (min-width: 1024px) 385px, 90vw"
            />
          </div>
        )}

        <div className="flex flex-1 flex-col items-center">
          <h3 className="mt-5 text-center text-[1.25rem] font-normal leading-none text-ui-card">
            {title}
          </h3>

          <div className="mt-12 grid w-full grid-cols-3 px-14 text-ui-card">
            <div className="flex flex-col items-center gap-2">
              <Icon name="users" size="lg" className="text-ui-card" />
              <span className="text-sm font-light" suppressHydrationWarning>{people} {t("people")}</span>
            </div>

            <div className="flex flex-col items-center gap-2">
              <Icon name="bed" size="lg" className="text-ui-card" />
              <span className="text-sm font-light" suppressHydrationWarning>{beds} {bedText}</span>
            </div>

            <div className="flex flex-col items-center gap-2">
              <Icon name="cube" size="lg" className="text-ui-card" />
              <span className="text-sm font-light">{size} m²</span>
            </div>
          </div>

          <div className="mt-12 pb-5">
            <span className="primary-button text-ui-card" suppressHydrationWarning>{t("More")}</span>
          </div>
        </div>
      </article>
    </Link>
  );
}
